/**
 * Contrasto WCAG 2.x misurato sui colori realmente calcolati dal browser.
 * Per una sfumatura si campiona tutta la linea: le linear-gradient con colori legacy
 * interpolano in sRGB (valori gamma), quindi si interpola sui canali 0-255.
 */
export type Rgb = readonly [number, number, number]

export type ContrastReport = {
  text: string
  /** Colori di fondo campionati: stop reali agli estremi. */
  stops: { color: string; ratio: number }[]
  worst: { color: string; ratio: number }
}

const SAMPLES = 200

export function parseRgbList(css: string): Rgb[] {
  const out: Rgb[] = []
  for (const m of css.matchAll(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+))?/g)) {
    // Un colore trasparente non e un fondo: "rgba(0,0,0,0)" non va letto come nero.
    if (m[4] !== undefined && Number(m[4]) === 0) continue
    out.push([Number(m[1]), Number(m[2]), Number(m[3])])
  }
  return out
}

function channel(v: number): number {
  const c = v / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

export function luminance([r, g, b]: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

export function toHex([r, g, b]: Rgb): string {
  return '#' + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase()
}

/** Contrasto del testo di un elemento sul suo fondo (sfumatura o tinta piena). */
export function measureElement(el: Element): ContrastReport {
  const style = getComputedStyle(el)
  const [text] = parseRgbList(style.color)
  const gradient = style.backgroundImage.includes('gradient') ? parseRgbList(style.backgroundImage) : []
  const fills = gradient.length >= 2 ? gradient : parseRgbList(style.backgroundColor)
  if (!text || fills.length === 0) throw new Error('Colori non leggibili per la misura del contrasto')

  const stops = fills.map((c) => ({ color: toHex(c), ratio: contrastRatio(text, c) }))
  let worst = stops.reduce((w, s) => (s.ratio < w.ratio ? s : w))

  for (let seg = 0; seg < fills.length - 1; seg++) {
    const a = fills[seg]
    const b = fills[seg + 1]
    for (let i = 0; i <= SAMPLES; i++) {
      const t = i / SAMPLES
      const c: Rgb = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
      const ratio = contrastRatio(text, c)
      if (ratio < worst.ratio) worst = { color: toHex(c), ratio }
    }
  }
  return { text: toHex(text), stops, worst }
}
